import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import Autocomplete from '@mui/material/Autocomplete';
import { wrapAutocompleteInputProps } from '@ui/components/common/TextField/text-input-normalize';
import TextField from '@mui/material/TextField';
import { ErrorWithIcon } from '@ui/components/common/TextField/ErrorWithIcon';
import { ArrowDown2 } from 'iconsax-react';
import { Box } from '@mui/material';
import { REGISTRATION_FIELD_FILL_BG } from '../registrationFormFieldStyles';
import InfoIconTooltip from '@ui/components/common/Tooltips/InfoIconTooltip';
const EAutocomplete = ({ name, control, label, options, rules, error, tooltipText, bgColor = REGISTRATION_FIELD_FILL_BG, disabled = false, multiple = true, trimWhitespace = true, getOptionLabel: customGetOptionLabel, filterOptions: customFilterOptions, renderOption: customRenderOption, listboxMaxHeight, ...rest }) => {
    const styledLabel = useMemo(() => !label ? undefined : (_jsxs(Box, { display: 'inline-flex', alignItems: 'center', gap: '4px', sx: { pointerEvents: 'auto' }, children: [label, tooltipText && (_jsx(Box, { sx: {
                    display: 'inline-flex',
                    alignItems: 'center',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                }, children: _jsx(InfoIconTooltip, { title: tooltipText, size: 'small', variant: 'dark' }) }))] })), [label, tooltipText]);
    return (_jsx(Controller, { name: name, control: control, rules: rules, render: ({ field }) => {
            const fieldValue = field.value != null && field.value !== '' ? String(field.value).trim() : '';
            const selectedSingleOption = !multiple && fieldValue
                ? (options.find((opt) => String(opt.value) === fieldValue) ?? null)
                : null;
            const displayValue = selectedSingleOption ??
                (!multiple && fieldValue
                    ? { label: fieldValue, value: fieldValue }
                    : null);
            const visibleOptions = options.filter((opt) => !opt.isClosed);
            const autocompleteOptions = displayValue &&
                !visibleOptions.some((opt) => String(opt.value) === String(displayValue.value))
                ? [displayValue, ...visibleOptions]
                : visibleOptions;
            return (_jsx(Autocomplete, { popupIcon: _jsx(ArrowDown2, { size: 16, color: '#454F5B' }), ...rest, options: autocompleteOptions, multiple: multiple, disabled: disabled, disablePortal: true, limitTags: 2, fullWidth: true, getOptionKey: (option) => String(option.value), getOptionLabel: (option) => {
                    const labelFn = customGetOptionLabel ?? ((o) => o.label);
                    return labelFn(option);
                }, filterOptions: customFilterOptions, value: multiple
                    ? Array.isArray(field.value)
                        ? options.filter((opt) => field.value.includes(opt.value))
                        : []
                    : displayValue, onChange: (_, value) => {
                    if (disabled)
                        return;
                    if (multiple) {
                        field.onChange(Array.isArray(value) ? value.map((opt) => opt.value) : []);
                    }
                    else {
                        field.onChange(value?.value ?? '');
                    }
                }, isOptionEqualToValue: (option, value) => String(option.value) === String(value.value), slotProps: {
                    chip: {
                        variant: 'soft',
                        size: 'small',
                        color: 'info'
                    },
                    listbox: listboxMaxHeight
                        ? {
                            sx: {
                                maxHeight: listboxMaxHeight,
                                overscrollBehavior: 'contain'
                            }
                        }
                        : undefined
                }, renderInput: (params) => (_jsxs(_Fragment, { children: [_jsx(TextField, { ...params, inputProps: wrapAutocompleteInputProps(params.inputProps, trimWhitespace), label: styledLabel, variant: 'filled', sx: {
                                '.MuiFilledInput-root': {
                                    backgroundColor: `${bgColor} !important`,
                                    borderRadius: '14px !important',
                                    width: '100% !important'
                                },
                                '& input:-webkit-autofill': {
                                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                    WebkitTextFillColor: 'inherit',
                                    transition: 'background-color 5000s ease-in-out 0s'
                                },
                                '& input:-webkit-autofill:focus': {
                                    WebkitBoxShadow: '0 0 0 1000px transparent inset !important',
                                    WebkitTextFillColor: 'inherit'
                                },
                                '.MuiInputBase-root': {
                                    backgroundColor: `${bgColor} !important`,
                                    borderRadius: '14px !important',
                                    width: '100% !important'
                                }
                            } }), _jsx(ErrorWithIcon, { error: error, mt: 0.5, ml: 0.5 })] })), renderOption: customRenderOption ??
                    ((props, option) => (_createElement("li", { ...props, key: option.value },
                        _jsxs(Box, { sx: { display: 'flex', flexDirection: 'column' }, children: [_jsx("span", { children: option.label }), !!option.description && (_jsx("span", { style: { fontSize: 12, color: '#6B7280' }, children: option.description }))] })))) }));
        } }));
};
export default EAutocomplete;
